import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Formik } from "formik";
import * as yup from "yup";
import { enqueueSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import PageWrapper from "components/PageWrapper";
import permissionStore from "../api";
import loadingStore from "core/utils/loading";
import { useAuth } from "app/authHooks";
import { hasPermission, Permission } from "core/utils/roles";

function AddEditPermission() {
  const initialValues = {
    name: "",
    description: "",
    entity: "",
  };

  const { attributes } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const isEdit = !!params?.id;
  const { loading } = loadingStore;

  const { getPermissionById, updatePermission, createPermission } =
    permissionStore;
  const [formValues, setFormValues] = useState<any>(initialValues);

  const canUpdatePermission = hasPermission(
    "update:permissions" as Permission
  );

  const permissionSchema = yup.object({
    name: yup
      .string()
      .required("Name is required")
      .matches(
        /^[a-z\-_]+:[a-z\-_]+$/,
        'Name must follow the format "action:entity". Only lowercase letters, hyphens, and underscores are allowed. Example: "create:investor-profile" or "update:adviser_onboarding"'
      ),
    description: yup
      .string()
      .required("Description is required")
      .min(2, "Description must be at least 2 characters")
      .max(255, "Description cannot be more than 255 characters"),
    entity: yup
      .string()
      .min(2, "Entity must be at least 2 characters")
      .max(50, "Entity cannot be more than 50 characters")
      .required("Entity is required")
      .matches(
        /^[A-Z\-_]+$/,
        'Entity must be uppercase letters, hyphens, and underscores only, e.g., "INVESTOR_PROFILE" or "ADVISER-ONBOARDING"'
      ),
  });

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    if (isEdit) {
      const data = await getPermissionById(params.id);
      setFormValues(data);
    } else {
      setFormValues(initialValues);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const payload = {
      name: values.name,
      description: values.description,
      entity: values.entity,
    };

    if (isEdit && params.id) {
      const response = await updatePermission(params.id, payload);
      if (response) {
        enqueueSnackbar("Permission updated successfully!", {
          variant: "success",
        });
        navigate("/security/permissions");
      }
    } else {
      const response = await createPermission(payload);
      if (response) {
        enqueueSnackbar("Permission created successfully!", {
          variant: "success",
        });
        navigate("/security/permissions");
      }
    }
  };

  const formatError = (error: any): string | undefined => {
    if (typeof error === "string") {
      return error;
    } else if (Array.isArray(error)) {
      return error.join(", ");
    } else if (typeof error === "object") {
      if (error?.type) {
        return error.type;
      }
      return "An error occurred";
    }
    return undefined;
  };

  const CardComponent = ({
    title,
    cardContent,
  }: {
    title: React.ReactNode;
    cardContent: React.ReactNode;
  }) => (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: "16px",
        width: "100%",
      }}
    >
      <header style={{ borderBottom: "1px solid #ddd", width: "100%" }}>
        {title}
      </header>
      <main style={{ marginTop: "20px" }}>{cardContent}</main>
    </div>
  );

  return (
    <PageWrapper>
      <Box sx={{ py: 4, px: 4 }}>
        <Box
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: "16px",
          }}
        >
          <Link
            underline="hover"
            onClick={() => navigate("/security/permissions")}
          >
            <ArrowBackIcon
              sx={{ width: "16px", height: "16px", marginRight: "4px" }}
            />{" "}
            Back to Permissions
          </Link>
        </Box>

        <Formik
          initialValues={formValues}
          validationSchema={permissionSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <CardComponent
                  title={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="h3"
                        margin={"10px"}
                        sx={{ fontWeight: 700 }}
                      >
                        {isEdit
                          ? canUpdatePermission
                            ? "Edit Permission"
                            : "View Permission"
                          : "Add Permission"}
                      </Typography>
                    </Box>
                  }
                  cardContent={
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <TextField
                            name="name"
                            label="Name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.name && Boolean(errors.name)}
                            helperText={
                              touched.name ? formatError(errors.name) : ""
                            }
                            InputLabelProps={{ shrink: true }}
                            disabled={!canUpdatePermission}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <TextField
                            name="description"
                            label="Description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.description && Boolean(errors.description)
                            }
                            helperText={
                              touched.description
                                ? formatError(errors.description)
                                : ""
                            }
                            InputLabelProps={{ shrink: true }}
                            disabled={!canUpdatePermission}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <TextField
                            name="entity"
                            label="Entity"
                            value={values.entity}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.entity && Boolean(errors.entity)}
                            helperText={
                              touched.entity ? formatError(errors.entity) : ""
                            }
                            InputLabelProps={{ shrink: true }}
                            disabled={!canUpdatePermission}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  }
                />
              </Box>

              <Button
                sx={{ mt: 3 }}
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !canUpdatePermission}
                endIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {isEdit
                  ? loading
                    ? "UPDATING"
                    : "UPDATE"
                  : loading
                    ? "SAVING"
                    : "SAVE"}
              </Button>
            </form>
          )}
        </Formik>
      </Box>
    </PageWrapper>
  );
}

export default observer(AddEditPermission);
